import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {useArrowDownPress, useArrowUpPress} from 'lib/customHooks';
import {CardType, ExecutionStatus, TaskType} from 'constants/dataEnum';
import {mergeRefs} from 'lib/refHelper';
import {find, selectDistinct} from 'lib/objectHelper';
import BroadcastTaskTransfer from './Tasks/BroadcastTaskTransfer';
import BroadcastDestinationVolume from './Tasks/BroadcastDestinationVolume';

const BroadcastTasksContainer = React.forwardRef(
  (
    {
      cardID,
      disallowEdit,
      isHighlighted,
      executionStatus,
      tasks,
      labware,
      labwareState,
      onEdit,
      onSelectTask,
      onArrowUpPress,
      onArrowDownPress,
      readOnly,
      reuseTips,
      onReuseTipsChange,
      showEditButton,
      runtimeMode,
    },
    ref,
  ) => {
    const taskRef = useRef(null);
    const aspirateTasks = tasks.filter((task) => task.type === TaskType.ASPIRATE);
    const dispenseTasks = tasks.filter((task) => task.type === TaskType.DISPENSE);

    const dispenseVolume = dispenseTasks?.length > 0 && dispenseTasks[0].volume;
    const countLabwareDefinitionID = () =>
      selectDistinct(
        tasks.map((item) => {
          const inUsedLabware = find(labwareState, {LabwareID: item.labwareID});
          return inUsedLabware?.IDPrefix;
        }),
      ).length;

    useArrowUpPress(taskRef, onArrowUpPress);
    useArrowDownPress(taskRef, onArrowDownPress);

    return (
      <div className="wrapper" ref={mergeRefs(taskRef, ref)} role="presentation">
        {aspirateTasks?.length > 0 && labware && (
          <div
            onClick={(e) => {
              onSelectTask(1, CardType.BROADCAST, tasks, cardID);
              e.stopPropagation();
            }}
          >
            <BroadcastTaskTransfer
              key={`task-broadcast-1-${cardID}`}
              disallowEdit={() => disallowEdit(1)}
              number={1}
              tasks={aspirateTasks}
              labware={labware}
              labwareState={labwareState}
              isHighlighted={
                isHighlighted?.type === CardType.BROADCAST &&
                isHighlighted?.cardID === cardID &&
                isHighlighted?.ID === 1
              }
              executionStatus={executionStatus(1)}
              readOnly={readOnly}
              onEdit={() => onEdit(1)}
              showEditButton={showEditButton}
              runtimeMode={runtimeMode}
            />
          </div>
        )}
        {dispenseTasks?.length > 0 && labware && (
          <div
            onClick={(e) => {
              onSelectTask(2, CardType.BROADCAST, tasks, cardID);
              e.stopPropagation();
            }}
          >
            <BroadcastTaskTransfer
              key={`task-broadcast-2-${cardID}`}
              disallowEdit={() => disallowEdit(2)}
              number={2}
              tasks={dispenseTasks}
              labware={labware}
              labwareState={labwareState}
              labwareDefinitionIDCount={countLabwareDefinitionID}
              isHighlighted={
                isHighlighted?.type === CardType.BROADCAST &&
                isHighlighted?.cardID === cardID &&
                isHighlighted?.ID === 2
              }
              executionStatus={executionStatus(2)}
              readOnly={readOnly}
              onEdit={() => onEdit(2)}
              showEditButton={showEditButton}
              runtimeMode={runtimeMode}
            />
          </div>
        )}
        {dispenseVolume && labware ? (
          <div
            onClick={(e) => {
              onSelectTask(3, CardType.BROADCAST, tasks, cardID);
              e.stopPropagation();
            }}
          >
            <BroadcastDestinationVolume
              key={`task-broadcast-3-${cardID}`}
              disallowEdit={() => disallowEdit(3)}
              executionStatus={executionStatus(3)}
              number={3}
              tasks={dispenseTasks}
              labware={labware}
              labwareState={labwareState}
              isHighlighted={
                isHighlighted?.type === CardType.BROADCAST &&
                isHighlighted?.cardID === cardID &&
                isHighlighted?.ID === 3
              }
              onEdit={() => onEdit(3)}
              showEditButton={showEditButton}
              reuseTips={reuseTips}
              onReuseTipsChange={onReuseTipsChange}
              runtimeMode={runtimeMode}
            />
          </div>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        )}
      </div>
    );
  },
);

BroadcastTasksContainer.propTypes = {
  cardID: PropTypes.number.isRequired,
  disallowEdit: PropTypes.func,
  executionStatus: PropTypes.string,
  isHighlighted: PropTypes.bool,
  tasks: PropTypes.array.isRequired,
  labware: PropTypes.object.isRequired,
  labwareState: PropTypes.object,
  onEdit: PropTypes.func,
  onSelectTask: PropTypes.func,
  onArrowUpPress: PropTypes.func,
  onArrowDownPress: PropTypes.func,
  readOnly: PropTypes.bool,
  reuseTips: PropTypes.bool,
  onReuseTipsChange: PropTypes.func,
  runtimeMode: PropTypes.bool.isRequired,
  showEditButton: PropTypes.bool,
};

BroadcastTasksContainer.defaultProps = {
  disallowEdit: () => {},
  executionStatus: ExecutionStatus.NONE,
  isHighlighted: false,
  labwareState: {},
  onEdit: () => {},
  onSelectTask: () => {},
  onArrowUpPress: () => {},
  onArrowDownPress: () => {},
  readOnly: false,
  reuseTips: true,
  onReuseTipsChange: () => {},
  showEditButton: false,
};

export default BroadcastTasksContainer;
